import { createGLTransaction, GLTransaction, GLTransactionEntry, deleteGLTransaction } from '../firebase/glTransactionServices';
import { getGLAccountByNumber } from '../firebase/glAccountServices';
import { MonthlyInventoryState, getMonthlyInventoryStateById, updateMonthlyInventoryStateWithGLTransaction, removeMonthlyInventoryStateGLTransaction } from '../firebase/cogsServices';
import { Timestamp } from 'firebase/firestore';

// Format currency helper function
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 2
  }).format(amount);
};

// Interface for GL entry preview
export interface GLEntryPreview {
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
  accountId?: string; // Will be populated when creating actual GL entries
}

// GL Account numbers for COGS and write-off
const INVENTORY_GL_ACCOUNT = { accountNumber: '1300002', name: 'Inventory Balances' };
const COGS_GL_ACCOUNT = { accountNumber: '8100001', name: 'COGS - Store' };
const WRITEOFF_GL_ACCOUNT = { accountNumber: '7300001', name: 'Inventory Write Off' };

/**
 * Generate GL entry previews for COGS
 * 
 * @param monthlyState Monthly inventory state data
 * @returns Array of GL entry previews
 */
export const generateCogsGLEntryPreviews = (monthlyState: MonthlyInventoryState): GLEntryPreview[] => {
  const entries: GLEntryPreview[] = [];
  
  // Calculate total COGS directly from the table
  const totalCOGS = monthlyState.products.reduce((sum, p) => sum + p.totalCOGS, 0);
  
  // Calculate total write-offs
  const totalWriteOffs = monthlyState.products.reduce((sum, p) => sum + (p.writeOffCost || 0), 0);
  
  // Handle COGS entries (can be positive or negative)
  if (Math.abs(totalCOGS) > 0.01) {
    if (totalCOGS > 0) {
      // Normal case: Positive COGS (expense)
      // Add debit entry for COGS
      entries.push({
        accountNumber: COGS_GL_ACCOUNT.accountNumber,
        accountName: COGS_GL_ACCOUNT.name,
        debit: totalCOGS,
        credit: 0
      });
      
      // Add credit entry for inventory
      entries.push({
        accountNumber: INVENTORY_GL_ACCOUNT.accountNumber,
        accountName: INVENTORY_GL_ACCOUNT.name,
        debit: 0,
        credit: totalCOGS
      });
    } else {
      // Negative COGS (unusual but possible)
      // Add credit entry for COGS (reducing expense)
      entries.push({
        accountNumber: COGS_GL_ACCOUNT.accountNumber,
        accountName: COGS_GL_ACCOUNT.name,
        debit: 0,
        credit: Math.abs(totalCOGS)
      });
      
      // Add debit entry for inventory (increasing inventory)
      entries.push({
        accountNumber: INVENTORY_GL_ACCOUNT.accountNumber,
        accountName: INVENTORY_GL_ACCOUNT.name,
        debit: Math.abs(totalCOGS),
        credit: 0
      });
    }
  }
  
  // Only add write-off entries if there's a positive write-off value
  if (totalWriteOffs > 0) {
    // Add debit entry for write-off
    entries.push({
      accountNumber: WRITEOFF_GL_ACCOUNT.accountNumber,
      accountName: WRITEOFF_GL_ACCOUNT.name,
      debit: totalWriteOffs,
      credit: 0
    });
    
    // Add credit entry for inventory
    entries.push({
      accountNumber: INVENTORY_GL_ACCOUNT.accountNumber,
      accountName: INVENTORY_GL_ACCOUNT.name,
      debit: 0,
      credit: totalWriteOffs
    });
  }
  
  return entries;
};

/**
 * Create GL transaction from monthly inventory state
 * 
 * @param monthlyState Monthly inventory state data
 * @param glEntryPreviews GL entry previews (optional - if not provided, will generate from monthlyState)
 * @returns Promise with the created GL transaction ID
 */
export const createCogsGLTransaction = async (
  monthlyState: MonthlyInventoryState,
  glEntryPreviews?: GLEntryPreview[]
): Promise<string> => {
  try {
    // If no previews provided, generate them
    const entries = glEntryPreviews || generateCogsGLEntryPreviews(monthlyState);
    
    // Validate that debits = credits
    const totalDebits = entries.reduce((sum, entry) => sum + entry.debit, 0);
    const totalCredits = entries.reduce((sum, entry) => sum + entry.credit, 0);
    
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      throw new Error('GL entries are not balanced. Total debits must equal total credits.');
    }
    
    // Convert entry previews to GL transaction entries
    const glEntries: GLTransactionEntry[] = [];
    
    for (const entry of entries) {
      // Get account ID by account number
      let accountId: string | undefined = entry.accountId;
      
      if (!accountId) {
        // Find the account by account number
        const account = await getGLAccountByNumber(entry.accountNumber);
        if (!account) {
          throw new Error(`GL Account with number ${entry.accountNumber} not found`);
        }
        accountId = account.id;
      }
      
      if (!accountId) {
        throw new Error(`Could not find GL Account ID for account number ${entry.accountNumber}`);
      }
      
      glEntries.push({
        accountId,
        accountNumber: entry.accountNumber,
        accountName: entry.accountName,
        debit: entry.debit,
        credit: entry.credit
      });
    }
    
    // Create the GL transaction
    const lastDay = new Date(monthlyState.year, monthlyState.month + 1, 0);
    
    // Get month name for better description
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[monthlyState.month];
    
    // Calculate totals for description
    const totalCOGS = entries.filter(e => e.accountNumber === COGS_GL_ACCOUNT.accountNumber).reduce((sum, e) => sum + e.debit, 0);
    const totalWriteOffs = entries.filter(e => e.accountNumber === WRITEOFF_GL_ACCOUNT.accountNumber).reduce((sum, e) => sum + e.debit, 0);
    
    // Create a more descriptive transaction description
    let description = '';
    if (totalCOGS > 0 && totalWriteOffs > 0) {
      description = `COGS (${formatCurrency(totalCOGS)}) and Write-offs (${formatCurrency(totalWriteOffs)}) for ${monthName} ${monthlyState.year}`;
    } else if (totalCOGS > 0) {
      description = `COGS for ${monthName} ${monthlyState.year} - ${formatCurrency(totalCOGS)}`;
    } else if (totalWriteOffs > 0) {
      description = `Inventory Write-offs for ${monthName} ${monthlyState.year} - ${formatCurrency(totalWriteOffs)}`;
    } else {
      description = `COGS and Write-offs for ${monthName} ${monthlyState.year}`;
    }
    
    const glTransaction: Omit<GLTransaction, 'id'> = {
      transactionDate: lastDay,
      description: description,
      referenceType: 'monthly_cogs',
      referenceId: `${monthlyState.year}-${monthlyState.month}`,
      entries: glEntries
    };
    
    // Create the transaction in Firestore
    const transactionId = await createGLTransaction(glTransaction);
    
    // Update the monthly inventory state with the GL transaction ID
    if (monthlyState.id) {
      await updateMonthlyInventoryStateWithGLTransaction(monthlyState.id, transactionId);
    }
    
    return transactionId;
  } catch (error) {
    console.error('Error creating COGS GL transaction:', error);
    throw error;
  }
};

/**
 * Reverse a monthly inventory state's GL transaction
 * 
 * @param monthlyStateId The ID of the monthly inventory state
 * @returns Promise that resolves when the reversal is complete
 */
export const reverseCogsGLTransaction = async (monthlyStateId: string): Promise<void> => {
  try {
    // Get the monthly inventory state
    const monthlyState = await getMonthlyInventoryState(monthlyStateId);
    
    if (!monthlyState) {
      throw new Error(`Monthly inventory state with ID ${monthlyStateId} not found`);
    }
    
    if (!monthlyState.glTransactionId) {
      throw new Error(`Monthly inventory state with ID ${monthlyStateId} does not have a GL transaction`);
    }
    
    // Delete the GL transaction
    await deleteGLTransaction(monthlyState.glTransactionId);
    
    // Remove the GL transaction ID from the monthly inventory state
    await removeMonthlyInventoryStateGLTransaction(monthlyStateId);
  } catch (error) {
    console.error('Error reversing COGS GL transaction:', error);
    throw error;
  }
};

// Helper function to get monthly inventory state by ID
const getMonthlyInventoryState = async (id: string): Promise<MonthlyInventoryState | null> => {
  return await getMonthlyInventoryStateById(id);
};
